/**
 * Design System - Button Component
 * Componente bottone standardizzato
 */

import * as React from 'react';
import { Button as MuiButton, ButtonProps as MuiButtonProps, CircularProgress } from '@mui/material';
import { styled } from '@mui/material/styles';

// Varianti personalizzate
export type ButtonVariant = 'primary' | 'secondary' | 'outlined' | 'text' | 'danger' | 'success';
export type ButtonSize = 'small' | 'medium' | 'large';

export interface ButtonProps extends Omit<MuiButtonProps, 'variant' | 'size'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  loadingText?: string;
}

// Styled component per varianti personalizzate
const StyledButton = styled(MuiButton, {
  shouldForwardProp: (prop) => !['loading', 'loadingText'].includes(prop as string),
})<ButtonProps>(({ theme, variant = 'primary', size = 'medium' }) => {
  // Mappa le varianti personalizzate a quelle standard di Material-UI
  const getMuiVariant = (customVariant: ButtonVariant): 'text' | 'outlined' | 'contained' => {
    switch (customVariant) {
      case 'primary':
      case 'secondary':
      case 'danger':
      case 'success':
        return 'contained';
      case 'outlined':
        return 'outlined';
      case 'text':
        return 'text';
      default:
        return 'contained';
    }
  };

  // Mappa i colori personalizzati a quelli standard di Material-UI
  const getMuiColor = (customVariant: ButtonVariant): 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (customVariant) {
      case 'primary':
        return 'primary';
      case 'secondary':
        return 'secondary';
      case 'danger':
        return 'error';
      case 'success':
        return 'success';
      case 'outlined':
      case 'text':
        return 'primary';
      default:
        return 'primary';
    }
  };

  const muiVariant = getMuiVariant(variant as ButtonVariant);
  const muiColor = getMuiColor(variant as ButtonVariant);
  const getVariantStyles = () => {
    switch (variant) {
      case 'primary':
        return {
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          '&:hover': {
            backgroundColor: theme.palette.primary.dark,
          },
        };
      case 'secondary':
        return {
          backgroundColor: theme.palette.secondary.main,
          color: theme.palette.secondary.contrastText,
          '&:hover': {
            backgroundColor: theme.palette.secondary.dark,
          },
        };
      case 'outlined':
        return {
          backgroundColor: 'transparent',
          color: theme.palette.primary.main,
          border: `1px solid ${theme.palette.primary.main}`,
          '&:hover': {
            backgroundColor: theme.palette.primary[50],
            borderColor: theme.palette.primary.dark,
          },
        };
      case 'text':
        return {
          backgroundColor: 'transparent',
          color: theme.palette.primary.main,
          '&:hover': {
            backgroundColor: theme.palette.primary[50],
          },
        };
      case 'danger':
        return {
          backgroundColor: theme.palette.error.main,
          color: theme.palette.error.contrastText,
          '&:hover': {
            backgroundColor: theme.palette.error.dark,
          },
        };
      case 'success':
        return {
          backgroundColor: theme.palette.success.main,
          color: theme.palette.success.contrastText,
          '&:hover': {
            backgroundColor: theme.palette.success.dark,
          },
        };
      default:
        return {};
    }
  };

  const getSizeStyles = () => {
    switch (size) {
      case 'small':
        return {
          padding: '4px 12px',
          fontSize: '0.75rem',
          minHeight: '32px',
        };
      case 'medium':
        return {
          padding: '8px 16px',
          fontSize: '0.875rem',
          minHeight: '40px',
        };
      case 'large':
        return {
          padding: '12px 24px',
          fontSize: '1rem',
          minHeight: '48px',
        };
      default:
        return {};
    }
  };

  return {
    ...getVariantStyles(),
    ...getSizeStyles(),
    borderRadius: theme.shape.borderRadius,
    textTransform: 'none',
    fontWeight: 500,
    boxShadow: 'none',
    '&:hover': {
      boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
    },
    '&:active': {
      boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.2)',
    },
    '&:disabled': {
      opacity: 0.6,
      cursor: 'not-allowed',
    },
  };
});

export const Button: React.FC<ButtonProps> = ({
  children,
  loading = false,
  loadingText,
  disabled,
  variant = 'primary',
  ...props
}) => {
  const isDisabled = disabled || loading;

  // Mappa le varianti personalizzate a quelle standard di Material-UI
  const getMuiVariant = (customVariant: ButtonVariant) => {
    switch (customVariant) {
      case 'primary':
      case 'secondary':
      case 'danger':
      case 'success':
        return 'contained';
      case 'outlined':
        return 'outlined';
      case 'text':
        return 'text';
      default:
        return 'contained';
    }
  };

  // Mappa i colori personalizzati a quelli standard di Material-UI
  const getMuiColor = (customVariant: ButtonVariant): 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (customVariant) {
      case 'primary':
        return 'primary';
      case 'secondary':
        return 'secondary';
      case 'danger':
        return 'error';
      case 'success':
        return 'success';
      case 'outlined':
      case 'text':
        return 'primary';
      default:
        return 'primary';
    }
  };

  const muiVariant = getMuiVariant(variant as ButtonVariant);
  const muiColor = getMuiColor(variant as ButtonVariant);

  return (
    <MuiButton
      {...props}
      variant={muiVariant}
      color={muiColor}
      disabled={isDisabled}
      startIcon={loading ? <CircularProgress size={16} color="inherit" /> : props.startIcon}
      sx={{
        textTransform: 'none',
        fontWeight: 500,
        borderRadius: 2,
        boxShadow: 'none',
        '&:hover': {
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
        },
        '&:active': {
          boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.2)',
        },
        '&:disabled': {
          opacity: 0.6,
          cursor: 'not-allowed',
        },
      }}
    >
      {loading ? loadingText || 'Caricamento...' : children}
    </MuiButton>
  );
};

export default Button;
